/**
 * Ted #19 → #939 (3a; #933 PR 4) — Settings → Lodging.
 * Ted #35 (his Item 33) — the zone field never looks filled, several beds in
 * one go, edit a bed, and the sentence that says where dogs meet beds.
 * Sawyer B3 — the manager sets up beds; the threshold stays with the business
 * owners (Sawyer, memo2: *"It says that the owner can do that. I'm not, I'm
 * just the manager."*; Ted: *"the money, you manage the money"*).
 *
 * Ted, 09-09: *"Each numbered space represents a bed … 15 available beds
 * downstairs. We created and set that number ourselves. It is an operational
 * capacity threshold and does not necessarily represent the literal number
 * of individual physical beds."*
 *
 * Ted, 10-06: *"I'm also getting 'no bed is free these nights,' even though I
 * set the capacity at 15. I went into Settings and tried several ways to
 * assign Charlie and Zooey to a downstairs kennel that holds two dogs, but
 * I'm getting an error saying the zone name isn't entered, even though
 * 'downstairs' is showing."* — the grey placeholder "Downstairs" read as a
 * value, the threshold read as the inventory, and nothing on this card said
 * that dogs are put on beds from the calendar, not here.
 *
 * Two facts, two controls, kept apart on purpose:
 *   · the BEDS — one row per bed / cubicle (name, zone, kind, dogs it holds),
 *     added inline (one, or "Dog" × 15 → Dog 1 … Dog 15), renamed, edited
 *     (zone / kind / dogs per bed), retired (never deleted: the calendar
 *     keeps where a dog slept);
 *   · the THRESHOLD — one number the lodging calendar's header turns amber
 *     approaching and red at. An operations key (`PUT /operations`), so it
 *     can be 15 while the inventory has 18 beds, exactly as Ted runs it.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the bed count and the threshold are shown as
 *    two numbers with two labels, never blended; the count is the rows; a
 *    placeholder is an example ("e.g. Downstairs"), never a value.
 *  - #2 The next action is obvious: one primary "Add a bed"; a validation
 *    error lands ON the field it names; per row Rename / Edit (inline) and a
 *    two-tap Retire with the consequence spelled out; the card says where
 *    dogs are put on beds and links there; a non-owner sees the inventory
 *    with a plain reason, never a dead control.
 *  - #5 Never lie about data: "Not set" is named as such for the threshold;
 *    a retired bed stays listed, marked Retired, so history reads true; a
 *    bulk add that stops part-way says exactly how many landed and which
 *    name was refused.
 *  - Five states: loading · error(+retry) · empty (no beds — teaching copy)
 *    · ready · saving (per-row / per-form in-flight lock, with progress).
 *
 * Counter Test: Ted types "Dog", 15, Downstairs, Kennel, 1 dog per bed and
 * taps Add 15 beds; fifteen rows appear, Beds in service reads 15, and the
 * lodging calendar (3b) has fifteen rows where Charlie and Zooey get a bed
 * from "Assign to bed…". No script, no session.
 *
 * Server contract: GET/POST /api/v2/admin/tenant/lodging/units,
 * PATCH /lodging/units/:id; GET/PUT /api/v2/admin/tenant/operations for the
 * threshold. Read business-config:read:all. Beds: business-config:write:all
 * OR business-config.lodging:write:all (owner + manager, Sawyer B3). The
 * threshold: business-config:write:all (owner, manager; #933 D2).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
// Sawyer 04 manager-beds (B3)
import { BED_WRITE_FORBIDDEN_MESSAGE, bedWriteErrorMessage } from './lodging-bed-write-error.js';
import { Alert, Badge, Button, Card, EmptyState, Input, Select, Skeleton } from '@ferro/ui';
import {
  adminTenantLodgingApi,
  adminTenantSettingsApi,
  type TenantLodgingUnit,
  type TenantLodgingUnitDraft,
  type TenantLodgingUnitPatch,
  type TenantLodgingView,
  type TenantOperationsSettings,
} from '../../lib/api.js';
import { intInput } from './capacity-helpers.js';
// Sawyer 06 beds-lockers-runcard (B2, B4) — a bed's size and default locker; the size list and locker switch.
import {
  MAX_LOCKER_LENGTH,
  NO_SIZES_HINT,
  defaultLockerValue,
  showSizeField,
  sizeLabel,
  sizeOptions,
} from './lodging-bed-fields.js';
import { LodgingPolicyControls } from './LodgingPolicyControls.js';

interface LodgingSettingsCardProps {
  apiUrl: string;
  /** Add, rename, edit and retire beds: business-config.lodging:write:all (owner + manager, Sawyer B3). */
  canEdit: boolean;
  /** The lodging capacity threshold (PUT /operations): business-config:write:all (owner, manager). */
  canEditThreshold: boolean;
}

type Status = 'loading' | 'error' | 'ready';

/** Mirrors the boundary schema's MAX_DOGS_PER_UNIT. */
export const MAX_DOGS_PER_BED = 10;
/** Mirrors MAX_LODGING_CAPACITY_THRESHOLD. */
export const MAX_LODGING_THRESHOLD = 5000;
/** How many beds one Add may create — Ted runs 15; 50 covers a big block without a runaway loop. */
export const MAX_BEDS_PER_ADD = 50;
const MAX_NAME_LENGTH = 40;

const ZONE_MESSAGE = 'Name the zone it is in, for example Downstairs.';
const KIND_MESSAGE = 'Pick what kind of space this is.';
const CAPACITY_MESSAGE = `Dogs per bed must be a whole number from 1 to ${MAX_DOGS_PER_BED}.`;

export interface BedForm {
  name: string;
  kind: string;
  zone: string;
  capacity: string;
  /** How many beds this Add creates; above 1 the name is a prefix with numbering. */
  count: string;
  // Sawyer 06 beds-lockers-runcard (B2) — '' = no size; absent = the field was not offered.
  sizeClass?: string;
}

export type BedField = keyof BedForm;

export const EMPTY_BED_FORM: BedForm = { name: '', kind: 'kennel', zone: '', capacity: '1', count: '1' };

/**
 * "Dog" × 15 → Dog 1 … Dog 15; "Dog 1" × 15 → Dog 1 … Dog 15; "Dog 16" × 5 →
 * Dog 16 … Dog 20 (a trailing number sets the start, and keeps its padding:
 * "Dog 01" × 3 → Dog 01 … Dog 03). One bed is just the name as typed.
 */
export function plannedBedNames(name: string, count: number): string[] {
  const base = name.trim();
  if (base.length === 0 || !Number.isInteger(count) || count < 1) return [];
  if (count === 1) return [base];
  const m = /^(.*?)(\d+)$/.exec(base);
  const prefix = m ? (m[1] as string) : `${base} `;
  const digits = m ? (m[2] as string) : '1';
  const start = Number(digits);
  return Array.from({ length: count }, (_, i) => `${prefix}${String(start + i).padStart(digits.length, '0')}`);
}

export type BedDraftResult =
  | { ok: true; drafts: TenantLodgingUnitDraft[] }
  | { ok: false; field: BedField; message: string };

/** The Add form → the POST bodies, or the one field that is wrong and why (so the error lands ON it). */
export function draftFromBedForm(f: BedForm, kinds: ReadonlyArray<{ key: string; label: string }>): BedDraftResult {
  const fail = (field: BedField, message: string) => ({ ok: false as const, field, message });
  const name = f.name.trim();
  if (name.length === 0 || name.length > MAX_NAME_LENGTH) return fail('name', `Give the bed a name the desk will recognise (up to ${MAX_NAME_LENGTH} characters).`);
  if (!kinds.some((k) => k.key === f.kind)) return fail('kind', KIND_MESSAGE);
  const zone = f.zone.trim();
  if (zone.length === 0 || zone.length > MAX_NAME_LENGTH) return fail('zone', ZONE_MESSAGE);
  const capacity = intInput(f.capacity, 1, MAX_DOGS_PER_BED);
  if (capacity === null || capacity === undefined) return fail('capacity', CAPACITY_MESSAGE);
  const count = intInput(f.count, 1, MAX_BEDS_PER_ADD);
  if (count === null || count === undefined) return fail('count', `How many must be a whole number from 1 to ${MAX_BEDS_PER_ADD}.`);
  const names = plannedBedNames(name, count);
  if (names.some((n) => n.length > MAX_NAME_LENGTH)) return fail('name', `With the numbers added, each name must stay within ${MAX_NAME_LENGTH} characters — try a shorter name.`);
  // Sawyer 06 beds-lockers-runcard (B2) — every bed in one Add gets the same size; no size = the key is left out.
  const size = f.sizeClass?.trim() ?? '';
  return { ok: true, drafts: names.map((n) => ({ name: n, kind: f.kind, zone, capacity, ...(size ? { sizeClass: size } : {}) })) };
}

export interface EditForm {
  kind: string;
  zone: string;
  capacity: string;
  // Sawyer 06 beds-lockers-runcard (B2, B4) — absent = not offered (an older server); '' = none.
  sizeClass?: string;
  defaultLocker?: string;
}

export type EditField = keyof EditForm;

export type EditPatchResult =
  | { ok: true; patch: TenantLodgingUnitPatch }
  | { ok: false; field: EditField; message: string };

/** The Edit form → ONLY the keys that differ from the bed as it is; an empty patch means nothing changed. */
export function editPatchFromForm(
  u: TenantLodgingUnit,
  f: EditForm,
  kinds: ReadonlyArray<{ key: string; label: string }>,
): EditPatchResult {
  const fail = (field: EditField, message: string) => ({ ok: false as const, field, message });
  if (!kinds.some((k) => k.key === f.kind)) return fail('kind', KIND_MESSAGE);
  const zone = f.zone.trim();
  if (zone.length === 0 || zone.length > MAX_NAME_LENGTH) return fail('zone', ZONE_MESSAGE);
  const capacity = intInput(f.capacity, 1, MAX_DOGS_PER_BED);
  if (capacity === null || capacity === undefined) return fail('capacity', CAPACITY_MESSAGE);
  const patch: TenantLodgingUnitPatch = {};
  if (f.kind !== u.kind) patch.kind = f.kind;
  if (zone !== u.zone) patch.zone = zone;
  if (capacity !== u.capacity) patch.capacity = capacity;
  // Sawyer 06 beds-lockers-runcard (B2, B4) — only what changed; '' clears.
  if (f.sizeClass !== undefined) {
    const size = f.sizeClass.trim() === '' ? null : f.sizeClass.trim();
    if (size !== (u.sizeClass ?? null)) patch.sizeClass = size;
  }
  if (f.defaultLocker !== undefined) {
    const locker = defaultLockerValue(f.defaultLocker);
    if (!locker.ok) return fail('defaultLocker', locker.message);
    if (locker.value !== (u.defaultLocker ?? null)) patch.defaultLocker = locker.value;
  }
  return { ok: true, patch };
}

/** The threshold field → the PUT value: blank clears (null), a number within range sets. */
export function thresholdPatch(raw: string, current: number | null): { ok: true; value: number | null } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const n = intInput(trimmed, 1, MAX_LODGING_THRESHOLD);
  if (n === null || n === undefined) return { ok: false, message: `The threshold must be a whole number from 1 to ${MAX_LODGING_THRESHOLD}, or blank for none.` };
  return { ok: true, value: n === current ? current : n };
}

function dogs(n: number): string {
  return `${n} ${n === 1 ? 'dog' : 'dogs'}`;
}

export function LodgingSettingsCard({ apiUrl, canEdit, canEditThreshold }: LodgingSettingsCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<TenantLodgingView | null>(null);
  const [ops, setOps] = useState<TenantOperationsSettings | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Focus the rename field when it opens (or moves to another bed).
  useEffect(() => {
    if (renameId !== null) renameInputRef.current?.focus();
  }, [renameId]);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ kind: 'kennel', zone: '', capacity: '1' });
  const [editError, setEditError] = useState<{ field: EditField; message: string } | null>(null);
  const [confirmRetireId, setConfirmRetireId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [bedForm, setBedForm] = useState<BedForm>(EMPTY_BED_FORM);
  /** `field` null = the server's answer (shown on the form); a field name = shown ON that field too. */
  const [addError, setAddError] = useState<{ field: BedField | null; message: string } | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addProgress, setAddProgress] = useState<{ done: number; total: number } | null>(null);
  const [thresholdInput, setThresholdInput] = useState('');
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  const [thresholdBusy, setThresholdBusy] = useState(false);
  // Sawyer 06 beds-lockers-runcard (B2, B4)
  const canEditPolicy = canEditThreshold;

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial'): Promise<boolean> => {
      if (mode === 'initial') {
        setStatus('loading');
        setLoadError(null);
        setSaved(null);
      }
      const [units, operations] = await Promise.all([
        adminTenantLodgingApi.list(apiUrl),
        adminTenantSettingsApi.getOperations(apiUrl),
      ]);
      if (units.error || !units.data) {
        if (mode === 'initial') {
          setLoadError(units.error ?? 'Could not load your beds.');
          setStatus('error');
        }
        return false;
      }
      setView(units.data);
      // The threshold rides the operations read; an older server without the
      // key reads as "not set" — never an invented number.
      if (operations.data) {
        setOps(operations.data);
        const t = operations.data.operations.lodgingCapacityThreshold ?? null;
        setThresholdInput(t === null ? '' : String(t));
      }
      setStatus('ready');
      return true;
    },
    [apiUrl],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const units = view?.units ?? [];
  const kinds = view?.kinds ?? [];
  const zones = useMemo(() => [...new Set(units.map((u) => u.zone))], [units]);
  const threshold = ops?.operations.lodgingCapacityThreshold ?? null;
  const thresholdSource = ops?.source.lodgingCapacityThreshold ?? 'default';
  // Sawyer 06 beds-lockers-runcard (B2) — undefined on an older server (no size field, no hint).
  const sizeList = ops?.operations.bedSizeClasses;

  const clearTransient = () => {
    setRowError(null);
    setSaved(null);
    setConfirmRetireId(null);
  };

  /** Open the Add form; a tenant that already has a zone never types it twice. */
  const openAdd = () => {
    setAddOpen(true);
    setAddError(null);
    clearTransient();
    const firstZone = zones[0];
    setBedForm((f) => (f.zone.trim() === '' && firstZone !== undefined ? { ...f, zone: firstZone } : f));
  };

  const setBedField = (key: BedField, value: string) => {
    setBedForm((f) => ({ ...f, [key]: value }));
    setAddError((e) => (e !== null && e.field === key ? null : e));
  };

  const setEditField = (key: EditField, value: string) => {
    setEditForm((f) => ({ ...f, [key]: value }));
    setEditError((e) => (e !== null && e.field === key ? null : e));
  };

  const submitAdd = useCallback(async () => {
    const parsed = draftFromBedForm(bedForm, kinds);
    if (!parsed.ok) {
      setAddError({ field: parsed.field, message: parsed.message });
      return;
    }
    const { drafts } = parsed;
    setIsAdding(true);
    setAddError(null);
    setSaved(null);
    // Sequential POSTs to the existing create route (no bulk endpoint): the
    // server keeps its duplicate rule per bed, and a refusal stops the run
    // with the beds already added still real.
    const added: TenantLodgingUnit[] = [];
    let refused: { name: string; error: string | null } | null = null;
    for (const draft of drafts) {
      if (drafts.length > 1) setAddProgress({ done: added.length, total: drafts.length });
      const res = await adminTenantLodgingApi.create(apiUrl, draft);
      if (res.error || !res.data) {
        refused = { name: draft.name, error: res.code === 'forbidden' ? BED_WRITE_FORBIDDEN_MESSAGE : (res.error ?? null) };
        break;
      }
      added.push(res.data.unit);
    }
    setIsAdding(false);
    setAddProgress(null);
    if (added.length > 0) await load('refresh');
    const first = added[0];
    const last = added[added.length - 1];
    if (refused !== null) {
      if (drafts.length === 1) {
        setAddError({ field: null, message: refused.error ?? 'Could not add that bed.' });
        return;
      }
      const why = refused.error ?? 'the server refused it';
      if (first === undefined || last === undefined) {
        setAddError({ field: null, message: `Stopped at ${refused.name} — nothing was added: ${why}` });
        return;
      }
      setAddError({
        field: null,
        message: `Added ${added.length} of ${drafts.length} beds (${first.name} … ${last.name}); they are on the lodging calendar. Stopped at ${refused.name}: ${why}`,
      });
      // Leave the rest queued: the name becomes the one refused, the count the beds still to add.
      const refusedName = refused.name;
      const remaining = drafts.length - added.length;
      setBedForm((f) => ({ ...f, name: refusedName, count: String(remaining) }));
      return;
    }
    if (first === undefined || last === undefined) return;
    setBedForm((f) => ({ ...f, name: '', count: '1' }));
    setSaved(
      added.length === 1
        ? `Added ${first.name} (${first.kindLabel.toLowerCase()}, ${first.zone}, ${dogs(first.capacity)}). It is on the lodging calendar now.`
        : `Added ${added.length} beds (${first.kindLabel.toLowerCase()}, ${first.zone}, ${dogs(first.capacity)} each): ${first.name} … ${last.name}. They are on the lodging calendar now.`,
    );
  }, [apiUrl, bedForm, kinds, load]);

  const patchUnit = useCallback(
    async (u: TenantLodgingUnit, patch: TenantLodgingUnitPatch, done: (unit: TenantLodgingUnit) => string) => {
      setBusyId(u.id);
      setRowError(null);
      setSaved(null);
      const res = await adminTenantLodgingApi.update(apiUrl, u.id, patch);
      setConfirmRetireId(null);
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: u.id, message: bedWriteErrorMessage(res, 'Could not change that bed.') });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setRenameId(null);
      setEditId(null);
      setSaved(done(res.data.unit));
    },
    [apiUrl, load],
  );

  const openEdit = (u: TenantLodgingUnit) => {
    setEditId(u.id);
    setEditForm({
      kind: u.kind,
      zone: u.zone,
      capacity: String(u.capacity),
      // Sawyer 06 beds-lockers-runcard (B2, B4) — offered only when the server carries them.
      ...(sizeList !== undefined ? { sizeClass: u.sizeClass ?? '' } : {}),
      ...(u.defaultLocker !== undefined ? { defaultLocker: u.defaultLocker ?? '' } : {}),
    });
    setEditError(null);
    setRenameId(null);
    clearTransient();
  };

  const submitEdit = useCallback(
    async (u: TenantLodgingUnit) => {
      const parsed = editPatchFromForm(u, editForm, kinds);
      if (!parsed.ok) {
        setEditError({ field: parsed.field, message: parsed.message });
        return;
      }
      if (Object.keys(parsed.patch).length === 0) {
        setEditId(null);
        setEditError(null);
        return;
      }
      await patchUnit(u, parsed.patch, (unit) => {
        // Sawyer 06 beds-lockers-runcard (B2, B4) — the size and default locker join the sentence when set.
        const size = sizeLabel(unit.sizeClass, sizeList);
        const extras = `${size ? ` · ${size}` : ''}${unit.defaultLocker ? ` · locker ${unit.defaultLocker}` : ''}`;
        return `${unit.name} is now ${unit.zone} · ${unit.kindLabel.toLowerCase()} · ${dogs(unit.capacity)}${extras}.`;
      });
    },
    [editForm, kinds, patchUnit, sizeList],
  );

  const saveThreshold = useCallback(async () => {
    const parsed = thresholdPatch(thresholdInput, threshold);
    if (!parsed.ok) {
      setThresholdError(parsed.message);
      return;
    }
    if (parsed.value === threshold) {
      setThresholdError(null);
      return;
    }
    setThresholdBusy(true);
    setThresholdError(null);
    setSaved(null);
    const res = await adminTenantSettingsApi.updateOperations(apiUrl, { lodgingCapacityThreshold: parsed.value });
    setThresholdBusy(false);
    if (res.error || !res.data) {
      setThresholdError(res.error ?? 'Could not save the threshold.');
      return;
    }
    setOps(res.data);
    const t = res.data.operations.lodgingCapacityThreshold ?? null;
    setThresholdInput(t === null ? '' : String(t));
    setSaved(
      t === null
        ? 'Lodging threshold cleared — the calendar shows the bed count alone.'
        : `Lodging threshold is ${t} — the calendar turns amber approaching it and red at it.`,
    );
  }, [apiUrl, threshold, thresholdInput]);

  // ── Loading ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Lodging">
        <div data-testid="lodging-loading">
          <Skeleton variant="text" lines={5} />
        </div>
      </Card>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (status === 'error' || !view) {
    return (
      <Card title="Lodging">
        <div data-testid="lodging-error">
          <Alert variant="error">{loadError ?? 'Could not load your beds.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const active = units.filter((u) => u.isActive);
  const plannedCount = intInput(bedForm.count, 1, MAX_BEDS_PER_ADD);
  const planned = plannedCount !== null && plannedCount !== undefined && plannedCount > 1 ? plannedBedNames(bedForm.name, plannedCount) : [];
  const planFirst = planned[0];
  const planLast = planned[planned.length - 1];
  const planText = planned.length > 1 && planFirst !== undefined && planLast !== undefined ? `Will add ${planFirst} … ${planLast}` : undefined;
  const fieldError = (field: BedField): string | undefined => (addError?.field === field ? addError.message : undefined);
  const editFieldError = (field: EditField): string | undefined => (editError?.field === field ? editError.message : undefined);

  return (
    <Card
      title="Lodging"
      subtitle="Your beds and cubicles, and the capacity threshold the lodging calendar warns at. The threshold is your number — it does not have to equal the bed count."
      headerRight={
        canEdit ? (
          <Button variant="primary" onClick={openAdd} data-testid="lodging-add">
            Add a bed
          </Button>
        ) : undefined
      }
    >
      <div className="ferro-settings__card" data-testid="lodging-ready">
        {saved ? (
          <div data-testid="lodging-saved">
            <Alert variant="success">{saved}</Alert>
          </div>
        ) : null}

        {/* ── The two numbers, side by side, never blended ─────────── */}
        <dl className="ferro-lodging__facts" data-testid="lodging-facts">
          <div className="ferro-lodging__fact">
            <dt>Beds in service</dt>
            <dd data-testid="lodging-bed-count">
              <span className="ferro-lodging__number">{active.length}</span>
              <span className="ferro-muted"> · room for {dogs(view.activeDogCapacity)}</span>
            </dd>
          </div>
          <div className="ferro-lodging__fact">
            <dt>Capacity threshold</dt>
            <dd data-testid="lodging-threshold">
              {canEditThreshold ? (
                <form
                  className="ferro-capacity__inline"
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveThreshold();
                  }}
                  aria-label="Lodging capacity threshold"
                >
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={MAX_LODGING_THRESHOLD}
                    step={1}
                    placeholder="Not set"
                    value={thresholdInput}
                    onChange={(e) => setThresholdInput(e.target.value)}
                    aria-label="Lodging capacity threshold (beds)"
                    data-testid="lodging-threshold-input"
                  />
                  <Button type="submit" size="sm" variant="primary" isLoading={thresholdBusy} data-testid="lodging-threshold-save">
                    Save
                  </Button>
                </form>
              ) : (
                <span className="ferro-lodging__number" data-testid="lodging-threshold-value">
                  {threshold ?? 'Not set'}
                </span>
              )}
              <span className="ferro-muted" data-testid="lodging-threshold-note">
                {' '}
                {threshold === null
                  ? 'Not set — the calendar shows the bed count alone.'
                  : thresholdSource === 'configured'
                    ? `Amber as bookings approach ${threshold}; red at ${threshold}.`
                    : 'Platform default.'}
              </span>
              {canEdit && !canEditThreshold ? (
                <p className="ferro-muted ferro-lodging__note" data-testid="lodging-threshold-readonly-note">
                  Only a business owner sets the capacity threshold.
                </p>
              ) : null}
            </dd>
            {thresholdError ? (
              <div data-testid="lodging-threshold-error">
                <Alert variant="error">{thresholdError}</Alert>
              </div>
            ) : null}
          </div>
        </dl>
        <LodgingPolicyControls apiUrl={apiUrl} ops={ops} canEdit={canEditPolicy} onSaved={(next, message) => { setOps(next); setSaved(message); }} />

        {/* ── Where beds and dogs meet — said where the owner is looking ── */}
        <p className="ferro-muted ferro-lodging__howto" data-testid="lodging-howto">
          Beds are the spaces you name here (Dog 1 … Dog 15). Dogs are put on a bed from the{' '}
          <Link to="/calendar/lodging" data-testid="lodging-howto-link">
            Lodging calendar
          </Link>{' '}
          — each stay there has 'Assign to bed…'.
        </p>

        {/* One datalist for both the Add and the Edit zone fields. */}
        <datalist id="lodging-zones">
          {zones.map((z) => (
            <option key={z} value={z} />
          ))}
        </datalist>

        {/* ── Add a bed, or several (inline) ──────────────────────────── */}
        {addOpen && canEdit ? (
          <form
            className="ferro-form ferro-lodging__add"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submitAdd();
            }}
            aria-label="Add a bed"
            data-testid="lodging-add-form"
          >
            <div className="ferro-settings__form-grid">
              <Input
                name="bedName"
                label="Bed name"
                maxLength={MAX_NAME_LENGTH}
                value={bedForm.name}
                onChange={(e) => setBedField('name', e.target.value)}
                placeholder="e.g. Dog 1"
                helperText={planText}
                error={fieldError('name')}
                data-testid="lodging-form-name"
              />
              <Input
                name="bedCount"
                label="How many"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_BEDS_PER_ADD}
                step={1}
                value={bedForm.count}
                onChange={(e) => setBedField('count', e.target.value)}
                helperText="Type Dog and 15 to add Dog 1 … Dog 15 in one go."
                error={fieldError('count')}
                data-testid="lodging-form-count"
              />
              <Select
                name="bedKind"
                label="Kind of space"
                value={bedForm.kind}
                onChange={(e) => setBedField('kind', e.target.value)}
                options={kinds.map((k) => ({ value: k.key, label: k.label }))}
                error={fieldError('kind')}
                data-testid="lodging-form-kind"
              />
              {/* Sawyer 06 beds-lockers-runcard (B2) — every bed in this Add gets this size. */}
              {showSizeField(sizeList) ? (
                <Select
                  name="bedSize"
                  label="Size"
                  value={bedForm.sizeClass ?? ''}
                  onChange={(e) => setBedField('sizeClass', e.target.value)}
                  options={sizeOptions(sizeList, null)}
                  data-testid="lodging-form-size"
                />
              ) : null}
              <Input
                name="bedZone"
                label="Zone"
                maxLength={MAX_NAME_LENGTH}
                list="lodging-zones"
                value={bedForm.zone}
                onChange={(e) => setBedField('zone', e.target.value)}
                placeholder="e.g. Downstairs"
                helperText="Beds group by zone on the calendar."
                error={fieldError('zone')}
                data-testid="lodging-form-zone"
              />
              <Input
                name="bedCapacity"
                label="Dogs per bed"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_DOGS_PER_BED}
                step={1}
                value={bedForm.capacity}
                onChange={(e) => setBedField('capacity', e.target.value)}
                helperText="Only dogs from the same family ever share a bed."
                error={fieldError('capacity')}
                data-testid="lodging-form-capacity"
              />
            </div>
            {sizeList !== undefined && sizeList.length === 0 ? (
              <p className="ferro-muted ferro-lodging__hint" data-testid="lodging-form-size-hint">{NO_SIZES_HINT}</p>
            ) : null}
            {addError ? (
              <div data-testid="lodging-form-error">
                <Alert variant="error">{addError.message}</Alert>
              </div>
            ) : null}
            <div className="ferro-form__actions">
              <Button type="submit" variant="primary" isLoading={isAdding} data-testid="lodging-form-create">
                {addProgress ? `Adding ${addProgress.done + 1} of ${addProgress.total}…` : planned.length > 1 ? `Add ${planned.length} beds` : 'Add bed'}
              </Button>
              <Button type="button" variant="ghost" disabled={isAdding} onClick={() => { setAddOpen(false); setAddError(null); }}>
                Done
              </Button>
            </div>
          </form>
        ) : null}

        {/* ── The beds ────────────────────────────────────────────── */}
        {units.length === 0 ? (
          <div data-testid="lodging-empty">
            <EmptyState
              title="No beds yet"
              description="Name each bed or cubicle here — Dog 1, Dog 2 … — with its zone and how many dogs it holds; you can add several in one go. Putting a dog on a bed happens on the Lodging calendar, not here, once the beds exist."
              action={canEdit ? { label: 'Add a bed', onClick: openAdd } : undefined}
            />
          </div>
        ) : (
          <ul className="ferro-lodging__list" data-testid="lodging-list">
            {units.map((u) => {
              const busy = busyId === u.id;
              const err = rowError?.id === u.id ? rowError.message : null;
              const renaming = renameId === u.id;
              const editing = editId === u.id;
              return (
                <li
                  key={u.id}
                  className={`ferro-lodging__row${u.isActive ? '' : ' ferro-lodging__row--retired'}`}
                  data-testid={`lodging-unit-${u.id}`}
                  data-active={u.isActive ? 'true' : 'false'}
                >
                  <div className="ferro-lodging__main">
                    {renaming ? (
                      <form
                        className="ferro-capacity__inline"
                        noValidate
                        onSubmit={(e) => {
                          e.preventDefault();
                          const name = renameValue.trim();
                          if (name.length === 0 || name.length > MAX_NAME_LENGTH) {
                            setRowError({ id: u.id, message: `Give the bed a name (up to ${MAX_NAME_LENGTH} characters).` });
                            return;
                          }
                          if (name === u.name) {
                            setRenameId(null);
                            return;
                          }
                          void patchUnit(u, { name }, (unit) => `${u.name} is now ${unit.name}.`);
                        }}
                        aria-label={`Rename ${u.name}`}
                      >
                        <Input
                          value={renameValue}
                          maxLength={MAX_NAME_LENGTH}
                          onChange={(e) => setRenameValue(e.target.value)}
                          aria-label={`New name for ${u.name}`}
                          ref={renameInputRef}
                          data-testid={`lodging-rename-input-${u.id}`}
                        />
                        <Button type="submit" size="sm" variant="primary" isLoading={busy} data-testid={`lodging-rename-save-${u.id}`}>
                          Save
                        </Button>
                        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setRenameId(null)} data-testid={`lodging-rename-cancel-${u.id}`}>
                          Cancel
                        </Button>
                      </form>
                    ) : (
                      <div className="ferro-lodging__name" data-testid={`lodging-unit-name-${u.id}`}>
                        {u.name}
                      </div>
                    )}
                    <div className="ferro-lodging__facts-row">
                      <span className="ferro-muted" data-testid={`lodging-unit-zone-${u.id}`}>
                        {u.zone} · {u.kindLabel}
                      </span>
                      {/* Sawyer 06 beds-lockers-runcard (B2, B4) — shown only when set. */}
                      {u.sizeClass ? (
                        <span className="ferro-lodging__bed-size" data-testid={`lodging-unit-size-${u.id}`}>
                          {sizeLabel(u.sizeClass, sizeList)}
                        </span>
                      ) : null}
                      {u.defaultLocker ? (
                        <span className="ferro-lodging__bed-locker" data-testid={`lodging-unit-locker-${u.id}`}>
                          locker {u.defaultLocker}
                        </span>
                      ) : null}
                      <span className="ferro-muted" data-testid={`lodging-unit-capacity-${u.id}`}>
                        {dogs(u.capacity)}
                      </span>
                      <span data-testid={`lodging-unit-status-${u.id}`}>
                        <Badge variant={u.isActive ? 'active' : 'closed'}>{u.isActive ? 'In service' : 'Retired'}</Badge>
                      </span>
                    </div>
                  </div>

                  {canEdit && !renaming && !editing ? (
                    <div className="ferro-lodging__actions">
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setRenameId(u.id); setRenameValue(u.name); setEditId(null); clearTransient(); }} data-testid={`lodging-rename-${u.id}`}>
                        Rename
                      </Button>
                      {u.isActive ? (
                        <>
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEdit(u)} data-testid={`lodging-edit-${u.id}`}>
                            Edit
                          </Button>
                          {confirmRetireId === u.id ? (
                            <>
                              <Button size="sm" variant="danger" isLoading={busy} onClick={() => void patchUnit(u, { isActive: false }, (unit) => `${unit.name} is retired. Past stays keep it; nothing new is assigned to it.`)} data-testid={`lodging-retire-confirm-${u.id}`}>
                                Retire bed
                              </Button>
                              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmRetireId(null)}>
                                Keep
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setConfirmRetireId(u.id); setSaved(null); }} data-testid={`lodging-retire-${u.id}`}>
                              Retire
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button size="sm" variant="ghost" isLoading={busy} onClick={() => void patchUnit(u, { isActive: true }, (unit) => `${unit.name} is back in service.`)} data-testid={`lodging-restore-${u.id}`}>
                          Put back in service
                        </Button>
                      )}
                    </div>
                  ) : null}

                  {/* ── Edit zone / kind / dogs per bed (inline; Save sends only what changed) ── */}
                  {editing && canEdit ? (
                    <form
                      className="ferro-form ferro-lodging__edit"
                      noValidate
                      onSubmit={(e) => {
                        e.preventDefault();
                        void submitEdit(u);
                      }}
                      aria-label={`Edit ${u.name}`}
                      data-testid={`lodging-edit-form-${u.id}`}
                    >
                      <div className="ferro-settings__form-grid">
                        <Select
                          id={`lodging-edit-kind-${u.id}`}
                          label="Kind of space"
                          value={editForm.kind}
                          onChange={(e) => setEditField('kind', e.target.value)}
                          options={kinds.map((k) => ({ value: k.key, label: k.label }))}
                          error={editFieldError('kind')}
                          data-testid={`lodging-edit-kind-${u.id}`}
                        />
                        <Input
                          id={`lodging-edit-zone-${u.id}`}
                          label="Zone"
                          maxLength={MAX_NAME_LENGTH}
                          list="lodging-zones"
                          value={editForm.zone}
                          onChange={(e) => setEditField('zone', e.target.value)}
                          placeholder="e.g. Downstairs"
                          error={editFieldError('zone')}
                          data-testid={`lodging-edit-zone-${u.id}`}
                        />
                        <Input
                          id={`lodging-edit-capacity-${u.id}`}
                          label="Dogs per bed"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={MAX_DOGS_PER_BED}
                          step={1}
                          value={editForm.capacity}
                          onChange={(e) => setEditField('capacity', e.target.value)}
                          helperText="Only dogs from the same family ever share a bed."
                          error={editFieldError('capacity')}
                          data-testid={`lodging-edit-capacity-${u.id}`}
                        />
                        {/* Sawyer 06 beds-lockers-runcard (B2, B4) */}
                        {editForm.sizeClass !== undefined && sizeList !== undefined && (sizeList.length > 0 || editForm.sizeClass !== '') ? (
                          <Select
                            id={`lodging-edit-size-${u.id}`}
                            label="Size"
                            value={editForm.sizeClass}
                            onChange={(e) => setEditField('sizeClass', e.target.value)}
                            options={sizeOptions(sizeList, u.sizeClass)}
                            data-testid={`lodging-edit-size-${u.id}`}
                          />
                        ) : null}
                        {editForm.defaultLocker !== undefined ? (
                          <Input
                            id={`lodging-edit-locker-${u.id}`}
                            label="Default locker"
                            maxLength={MAX_LOCKER_LENGTH}
                            value={editForm.defaultLocker}
                            onChange={(e) => setEditField('defaultLocker', e.target.value)}
                            placeholder="e.g. 7"
                            helperText="Optional. Leave blank if lockers don't follow this bed."
                            error={editFieldError('defaultLocker')}
                            data-testid={`lodging-edit-locker-${u.id}`}
                          />
                        ) : null}
                      </div>
                      {sizeList !== undefined && sizeList.length === 0 && editForm.sizeClass === '' ? (
                        <p className="ferro-muted ferro-lodging__hint" data-testid={`lodging-edit-size-hint-${u.id}`}>{NO_SIZES_HINT}</p>
                      ) : null}
                      <div className="ferro-form__actions">
                        <Button type="submit" size="sm" variant="primary" isLoading={busy} data-testid={`lodging-edit-save-${u.id}`}>
                          Save
                        </Button>
                        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => { setEditId(null); setEditError(null); }}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : null}

                  {confirmRetireId === u.id ? (
                    <p className="ferro-muted ferro-lodging__note" data-testid={`lodging-retire-note-${u.id}`}>
                      {u.name} stops being offered for new stays. Stays already on it keep it, and you can put it back any time.
                    </p>
                  ) : null}

                  {err ? (
                    <div className="ferro-lodging__error" data-testid={`lodging-unit-error-${u.id}`}>
                      <Alert variant="error">{err}</Alert>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {!canEdit ? (
          <p className="ferro-muted" data-testid="lodging-readonly-note">
            Business owners and managers change beds and the capacity threshold.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
